import TelemetryButton from "@/design-system/telemetry-button";
import { type ITelemetryCustom } from "@/lib/telemetry";
import { CashbackABTest } from "@/src/cashback/LandingPage/_lp_ab_tests";
import useConfirmModal from "@/src/cashback/LandingPage/useConfirmModal";
import type { IInformationModal } from "@/src/common/InformationModal";
import { languageSelector } from "@/src/contexts";
import type { IPluginTopComponent } from "@/src/plugin/shared/PluginTopComponent";
import PluginTopComponent from "@/src/plugin/shared/PluginTopComponent";
import PageLayout from "@/src/shared/page-layout";
import type { IDiscountTileV3 } from "@Components/discount-tile/discount-tile-v3";
import {
    FaqLandingPage,
    allStoresCategoriesPanelId,
    whatIsCashbackPanelId,
} from "@Constants/PortalConstants";
import * as stylex from "@stylexjs/stylex";

import DiscountsSection from "../../homepage/Discounts/DiscountsSection";
import PanelSection from "../../shared/panel-section";
import CashbackFAQ, { type IFaqQuestionAnswers } from "./CashbackFAQ";
import { CashbackLandingPageResources } from "./CashbackLandingPage.Resources";
import { CashbackLandingPageResourcesEn } from "./CashbackLandingPage.Resources.En";
import type { ICashbackNumbersItem } from "./CashbackNumbers";
import CashbackNumbers from "./CashbackNumbers";
import type { ICashbackTopSection } from "./CashbackTopSection";
import type { IGoodieAdvantages } from "./GoodieAdvantages";
import GoodieAdvantages from "./GoodieAdvantages";
import MediaAboutUs from "./MediaAboutUs";
import type { IPopularCashbackBrands } from "./PopularCashbackBrands";
import PopularCashbackBrands from "./PopularCashbackBrands";
import { type ISecondRegistrationBanner, SecondRegistrationBanner } from "./SecondRegistrationBanner";
import WhatIsCashback from "./WhatIsCashback";

import * as css from "./CashbackLandingPage.module.css";

export type ICashbackLandingPage = {
    cashbackTopSection: ICashbackTopSection;
    cashbackNumbers: ICashbackNumbersItem[];
    goodieAdvantages: IGoodieAdvantages;
    secondRegistrationBanner: ISecondRegistrationBanner;
    popularCashbackBrands: IPopularCashbackBrands;
    discountsSection?: IDiscountTileV3[];
    faqList: IFaqQuestionAnswers[];
    confirmModal?: IInformationModal;
    pluginTopComponent?: IPluginTopComponent;
    variant: string;
};

const styles = stylex.create({
    pluginOverride: {
        paddingBottom: 0,
    },
});

export const CashbackLandingPage = (props: ICashbackLandingPage) => {
    const resources = languageSelector(CashbackLandingPageResources, CashbackLandingPageResourcesEn);

    const confirmModal = useConfirmModal(props.confirmModal, true);

    const viewTelemetry = (eventName: string) =>
        ({
            eventName: `cshbck-${eventName}`,
            actionType: "View",
        }) satisfies ITelemetryCustom;

    return (
        <>
            <PageLayout breadcrumbs={[{ label: "Cashback" }]} isNewGrid />
            <div className={css.cashbackLandingPageComponent}>
                <CashbackABTest variant={props.variant} />

                <PanelSection
                    {...resources.whatIsCashback}
                    id={whatIsCashbackPanelId}
                    telemetryCustom={viewTelemetry("WhatIsCashback")}
                    initialInView
                >
                    <WhatIsCashback
                        videoButtonText={resources.whatIsCashback.videoTitle}
                        showVideoButton={false}
                    />
                </PanelSection>

                <PanelSection
                    {...resources.popularCashbackBrands}
                    id={allStoresCategoriesPanelId}
                    telemetryCustom={viewTelemetry("PopularCashbackStores")}
                    initialInView
                >
                    <PopularCashbackBrands {...props.popularCashbackBrands} />
                </PanelSection>

                <PluginTopComponent {...props.pluginTopComponent} styleX={styles.pluginOverride} />

                {props.discountsSection?.length > 0 && (
                    <PanelSection
                        {...resources.discountsSection}
                        telemetryCustom={viewTelemetry("Discounts")}
                        initialInView
                    >
                        <DiscountsSection initialData={props.discountsSection} />
                    </PanelSection>
                )}

                <PanelSection
                    {...resources.goodieAdvantages}
                    telemetryCustom={viewTelemetry("GoodieAdvantages")}
                    initialInView
                >
                    <GoodieAdvantages {...props.goodieAdvantages} />
                </PanelSection>

                <PanelSection telemetryCustom={viewTelemetry("InNumbers")} initialInView>
                    <CashbackNumbers numbersList={props.cashbackNumbers} />
                </PanelSection>

                <PanelSection
                    {...resources.faqCashback}
                    telemetryCustom={viewTelemetry("FrequentlyAskedQuestions")}
                    initialInView
                >
                    <CashbackFAQ frequentQuestionsList={props.faqList} />
                    <div className={css.buttonContainer}>
                        <TelemetryButton
                            as="a"
                            variant="transparent"
                            size="M"
                            href={FaqLandingPage}
                            eventName="cshbck-FaqSeeMore"
                            isExternal
                        >
                            {resources.faqCashbackButton}
                        </TelemetryButton>
                    </div>
                </PanelSection>

                <PanelSection
                    {...resources.mediaAboutUs}
                    telemetryCustom={viewTelemetry("MediaAboutUs")}
                    initialInView
                >
                    <MediaAboutUs />
                </PanelSection>

                <SecondRegistrationBanner
                    {...props.secondRegistrationBanner}
                    {...resources.secondRegistrationBanner}
                    telemetryCustom={viewTelemetry("SecondRegistrationBanner")}
                />
                {confirmModal}
            </div>
        </>
    );
};
